// Small reusable HTML components.
import { esc } from '../dom.js';
import { icon } from '../brand.js';
import { headwearOf, type CosmeticContent, type HelmetDesign, type JerseyDesign, type ThemeContent } from '@gruvetto/contracts';
import { resolveLook } from '@gruvetto/creations';
import { jerseyDataUrl, paint } from '../../render/jersey.js';

/** Preview of a jersey (official or created pattern), rendered by the shared painter. */
export function jerseySwatch(look: any = {}, color = '#ffd400', size = 46) {
  return designSwatch(resolveLook(look).jersey, color, size);
}
export function designSwatch(design: JerseyDesign, color = '#ffd400', size = 46) {
  return `<img class="jersey" alt="" width="${size}" height="${size}" src="${jerseyDataUrl(design, color)}">`;
}

/** Tile of an activity in a list: workout, online race or solo race. */
export const activityIcon = (a: { type?: string; online?: boolean }) => `<div class="ico">${icon(a.type === 'workout' ? 'activity' : a.online ? 'globe' : 'flag', 18)}</div>`;
/** Leaderboard rank: gold, silver, bronze for the podium. */
export const rankBadge = (r: number) => `<span class="rank ${r <= 3 ? 'r' + r : ''}">${r}</span>`;
/** Empty state of a card: pictogram, title, explanation and action. */
export const emptyState = (ic: string, title: string, text = '', actions = '') =>
  `<div class="empty-state"><div class="es-ic">${icon(ic, 26)}</div><b>${title}</b>${text ? `<p>${text}</p>` : ''}${actions ? `<div class="inline">${actions}</div>` : ''}</div>`;
export const tile = (v: string | number, k: string, unit = '') => `<div class="tile"><div class="v">${v}${unit ? `<small> ${esc(unit)}</small>` : ''}</div><div class="k">${esc(k)}</div></div>`;

// ---------- Thumbnails of other cosmetics (frame, helmet, wheels) ----------
const FINISH_SHINE: Record<string, number> = { brillant: 0.35, mat: 0, metal: 0.25, chrome: 0.7, nacre: 0.45, carbone: 0.15 };
const icons = new Map<string, string>();

/** The head from the side, with what sits on it: helmet, nothing (hair shows), or a hat; the glasses in every case. */
function drawHeadwear(g: CanvasRenderingContext2D, d: HelmetDesign, team: string, S: number, shine: (x: number, y: number, r: number, k: number) => void) {
  const col = paint(d.paint, team), stripe = d.stripe ? paint(d.stripe, team) : shadeOf(col), shape = headwearOf(d);
  const cx = S / 2, cy = S * 0.58, dome = (rx: number, ry: number, color: string) => { g.fillStyle = color; g.beginPath(); g.ellipse(cx, cy, rx, ry, 0, Math.PI, 0); g.closePath(); g.fill(); };
  if (shape === 'none') {
    // a bare head: skin and hair
    g.fillStyle = '#d9a78c'; g.beginPath(); g.ellipse(cx, cy + S * 0.02, S * 0.3, S * 0.34, 0, 0, 7); g.fill();
    dome(S * 0.32, S * 0.3, '#4a3222');
  } else if (shape === 'road') {
    dome(S * 0.36, S * 0.3, col);
    if (d.stripe) { g.fillStyle = paint(d.stripe, team); g.beginPath(); g.ellipse(cx, cy, S * 0.07, S * 0.3, 0, Math.PI, 0); g.fill(); }
  } else if (shape === 'cap') {
    dome(S * 0.33, S * 0.26, col);
    g.fillStyle = stripe; g.fillRect(cx - S * 0.33, cy - S * 0.07, S * 0.66, S * 0.05);
    g.fillStyle = col; g.beginPath(); g.ellipse(cx + S * 0.3, cy - S * 0.01, S * 0.18, S * 0.05, 0, 0, 7); g.fill();
  } else if (shape === 'beanie') {
    dome(S * 0.34, S * 0.32, col);
    g.fillStyle = stripe; g.beginPath(); g.arc(cx, cy - S * 0.36, S * 0.09, 0, 7); g.fill();
    g.fillStyle = col; g.fillRect(cx - S * 0.36, cy - S * 0.08, S * 0.72, S * 0.1);
  } else if (shape === 'bucket') {
    dome(S * 0.3, S * 0.26, col); g.fillStyle = col; g.fillRect(cx - S * 0.3, cy - S * 0.1, S * 0.6, S * 0.1);
    g.beginPath(); g.moveTo(cx - S * 0.3, cy - S * 0.02); g.lineTo(cx - S * 0.46, cy + S * 0.1); g.lineTo(cx + S * 0.46, cy + S * 0.1); g.lineTo(cx + S * 0.3, cy - S * 0.02); g.fill();
    g.fillStyle = stripe; g.fillRect(cx - S * 0.3, cy - S * 0.1, S * 0.6, S * 0.04);
  } else if (shape === 'cowboy') {
    g.fillStyle = col; g.beginPath(); g.ellipse(cx, cy, S * 0.46, S * 0.08, 0, 0, 7); g.fill();
    g.fillRect(cx - S * 0.22, cy - S * 0.36, S * 0.44, S * 0.36);
    g.fillStyle = stripe; g.fillRect(cx - S * 0.23, cy - S * 0.1, S * 0.46, S * 0.05);
  } else if (shape === 'crown') {
    g.fillStyle = col; g.beginPath(); g.moveTo(cx - S * 0.34, cy); g.lineTo(cx - S * 0.34, cy - S * 0.3);
    for (let i = 0; i < 4; i++) { g.lineTo(cx - S * 0.34 + S * 0.17 * (i + 0.5), cy - S * 0.14); g.lineTo(cx - S * 0.34 + S * 0.17 * (i + 1), cy - S * 0.3); }
    g.lineTo(cx + S * 0.34, cy); g.closePath(); g.fill();
    g.fillStyle = stripe; for (let i = 0; i < 4; i++) { g.beginPath(); g.arc(cx - S * 0.34 + S * 0.17 * (i + 0.5), cy - S * 0.07, S * 0.03, 0, 7); g.fill(); }
  } else {
    g.fillStyle = col; g.fillRect(cx - S * 0.26, cy - S * 0.56, S * 0.52, S * 0.56);
    g.beginPath(); g.ellipse(cx, cy, S * 0.42, S * 0.06, 0, 0, 7); g.fill();
    g.fillStyle = stripe; g.fillRect(cx - S * 0.27, cy - S * 0.12, S * 0.54, S * 0.06);
  }
  // sunglasses below whatever is worn, their lens as designed
  g.fillStyle = { fume: '#20242c', miroir: '#8fb8e8', or: '#d8ab30', clair: '#c8d0d8' }[d.lens || 'fume']; g.fillRect(S * 0.24, cy + S * 0.02, S * 0.52, S * 0.1);
  if (shape !== 'none') shine(S * 0.42, S * 0.4, S * 0.24, FINISH_SHINE[d.finish] ?? 0.3);
}
/** A darker shade of a paint for a band nobody chose. */
const shadeOf = (hex: string) => { const n = parseInt(hex.slice(1), 16); return '#' + [16, 8, 0].map(sh => Math.round(((n >> sh) & 255) * 0.55).toString(16).padStart(2, '0')).join(''); };

function drawCosmetic(g: CanvasRenderingContext2D, c: Exclude<CosmeticContent, { slot: 'jersey' }>, team: string, S: number) {
  g.fillStyle = '#1a1f27'; g.fillRect(0, 0, S, S);
  g.lineCap = 'round'; g.lineJoin = 'round';
  const shine = (x: number, y: number, r: number, k: number) => { if (k <= 0) return; const gr = g.createRadialGradient(x - r * 0.3, y - r * 0.4, 0, x, y, r); gr.addColorStop(0, `rgba(255,255,255,${k})`); gr.addColorStop(1, 'rgba(255,255,255,0)'); g.fillStyle = gr; g.beginPath(); g.arc(x, y, r, 0, 7); g.fill(); };
  if (c.slot === 'frame') {
    const d = c.design, col = paint(d.paint, team), acc = d.accent ? paint(d.accent, team) : col;
    const BB = [S * 0.45, S * 0.68], ST = [S * 0.38, S * 0.3], HT = [S * 0.72, S * 0.32], R = [S * 0.2, S * 0.68], F = [S * 0.82, S * 0.68];
    const line = (a: number[], b: number[], w: number, color: string) => { g.strokeStyle = color; g.lineWidth = w; g.beginPath(); g.moveTo(a[0], a[1]); g.lineTo(b[0], b[1]); g.stroke(); };
    for (const w of [R, F]) { g.strokeStyle = '#3a3f48'; g.lineWidth = S * 0.025; g.beginPath(); g.arc(w[0], w[1], S * 0.15, 0, 7); g.stroke(); }
    line(BB, R, S * 0.04, acc); line(ST, R, S * 0.035, acc); line(HT, F, S * 0.045, acc);
    line(BB, ST, S * 0.055, col); line(ST, HT, S * 0.05, col); line(BB, HT, S * 0.065, col);
    shine(S * 0.55, S * 0.45, S * 0.25, FINISH_SHINE[d.finish] ?? 0.3);
  } else if (c.slot === 'helmet') {
    drawHeadwear(g, c.design, team, S, shine);
  } else {
    const d = c.design, rim = paint(d.rim, team), depth = { bas: 0.05, moyen: 0.1, haut: 0.17, lenticulaire: 0.33 }[d.depth];
    g.fillStyle = { noir: '#141414', beige: '#c9a26b', blanc: '#eeeeee' }[d.sidewall || 'noir']; g.beginPath(); g.arc(S / 2, S / 2, S * 0.42, 0, 7); g.fill();
    g.fillStyle = '#141414'; g.beginPath(); g.arc(S / 2, S / 2, S * 0.39, 0, 7); g.fill();
    g.fillStyle = rim; g.beginPath(); g.arc(S / 2, S / 2, S * 0.37, 0, 7); g.arc(S / 2, S / 2, S * (0.37 - depth), 0, 7, true); g.fill();
    if (d.depth !== 'lenticulaire') { g.strokeStyle = '#c9ccd2'; g.lineWidth = 1.5; for (let i = 0; i < 20; i++) { const a = (i / 20) * Math.PI * 2; g.beginPath(); g.moveTo(S / 2, S / 2); g.lineTo(S / 2 + Math.cos(a) * S * (0.37 - depth), S / 2 + Math.sin(a) * S * (0.37 - depth)); g.stroke(); } }
    g.fillStyle = d.hub ? paint(d.hub, team) : '#c9ccd2'; g.beginPath(); g.arc(S / 2, S / 2, S * 0.05, 0, 7); g.fill();
  }
}

/** Thumbnail of a cosmetic, whatever its slot. */
export function cosmeticSwatch(c: CosmeticContent, color = '#ffd400', size = 46) {
  if (c.slot === 'jersey') return designSwatch(c.design, color, size);
  const key = color + JSON.stringify(c);
  let u = icons.get(key);
  if (!u) {
    const cv = document.createElement('canvas'); cv.width = cv.height = 128;
    drawCosmetic(cv.getContext('2d')!, c, color, 128);
    u = cv.toDataURL('image/png');
    if (icons.size > 200) icons.delete(icons.keys().next().value!);
    icons.set(key, u);
  }
  return `<img class="jersey" alt="" width="${size}" height="${size}" src="${u}">`;
}

/** Thumbnail of an atmosphere: sky, horizon haze, fields and grass, weather icon. */
export function themeSwatch(t: ThemeContent, h = 54) {
  const sky = t.sky, land = t.land, sun = Math.max(4, Math.min(80, 70 - sky.sunElevation));
  const fields = land.fields.slice(0, 4).map((f, i, a) => `${f} ${Math.round((i / a.length) * 100)}% ${Math.round(((i + 1) / a.length) * 100)}%`).join(', ');
  const icon = { clair: '', pluie: '🌧️', neige: '❄️', brume: '🌫️', orage: '⛈️' }[t.weather];
  return `<span class="theme-sw" style="height:${h}px;background:radial-gradient(circle at 70% ${sun}%, ${sky.sun} 0 7%, transparent 9%), linear-gradient(${sky.sky}, ${sky.fog} 58%, transparent 58%), linear-gradient(90deg, ${fields})">${icon ? `<i>${icon}</i>` : ''}<b style="background:linear-gradient(90deg, ${land.grass}, ${land.grassLight})"></b></span>`;
}
